/* eslint-disable no-unused-vars */
import { createContext, useContext, ReactNode } from 'react';
import Teachr from '../Objects/models/Teachr';
import {
    AddressData,
    AddressErrors,
    BirthBankData,
    BirthBankErrors,
    ConditionsData,
    ConditionsErrors,
    PersonalInfoData,
    PersonalInfoErrors,
} from '../Hooks/InterfaceAiciOnboarding';

/**
 * Valeur partagée entre les étapes du parcours d'inscription AICI.
 * Construite une seule fois par le hook `useAiciOnboarding` dans l'orchestrateur,
 * puis consommée par chaque étape via `useAiciOnboardingContexte()` aucun prop-drilling.
 */
export interface AiciOnboardingContextValue {
    // ── Données chargées (1 seul fetch du TeachrContact) ──
    teachr?: Teachr;
    /** Prénom du professeur (affichage). */
    teachrFirstName?: string;
    /** Chargement initial du contact. */
    loading: boolean;
    /** Contact introuvable / supprimé. */
    notFound: boolean;

    // ── Navigation ──
    currentStep: number;
    /** Étape enregistrée côté serveur (0 = contact jamais commencé). */
    serverStep: number;
    /** Navigation libre (Précédent, liens d'édition du récapitulatif) sans sauvegarde. */
    goToStep: (step: number) => void;
    /** Persiste les données saisies (TeachrContact + Redux) puis navigue. */
    saveAndGoTo: (nextStep: number) => Promise<void>;
    /** Sauvegarde en cours (désactive les boutons). */
    saving: boolean;

    // ── Étape 1 : informations personnelles ──
    personal: PersonalInfoData;
    personalErrors: PersonalInfoErrors;
    setPersonal: (patch: Partial<PersonalInfoData>) => void;
    validatePersonal: () => boolean;

    // ── Étape 2 : naissance + banque ──
    birthBank: BirthBankData;
    birthBankErrors: BirthBankErrors;
    setBirthBank: (patch: Partial<BirthBankData>) => void;
    validateBirthBank: () => boolean;
    /** Dérivé : pays de naissance === France. */
    isBornFrance: boolean;

    // ── Étape 3 : adresse ──
    address: AddressData;
    addressErrors: AddressErrors;
    setAddress: (patch: Partial<AddressData>) => void;
    validateAddress: () => boolean;

    // ── Étape 4 : conditions + soumission ──
    conditions: ConditionsData;
    conditionsErrors: ConditionsErrors;
    setConditions: (patch: Partial<ConditionsData>) => void;
    validateConditions: () => boolean;
    /** Orchestration de création des comptes (idempotente). */
    submit: () => Promise<void>;
    submitting: boolean;

    // ── Mode self (activation depuis l'espace client) ──
    /** Vrai quand le parcours est lancé depuis l'espace client. */
    isSelf?: boolean;
    /** Code d'invitation du prof (activation autonome), optionnel. */
    invitationCode?: string;
    setInvitationCode?: (value: string) => void;
}

const AiciOnboardingContexte = createContext<AiciOnboardingContextValue | null>(null);

export const useAiciOnboardingContexte = (): AiciOnboardingContextValue => {
    const context = useContext(AiciOnboardingContexte);
    if (!context) {
        throw new Error(
            "useAiciOnboardingContexte doit être utilisé à l'intérieur d'un AiciOnboardingProvider"
        );
    }
    return context;
};

interface AiciOnboardingProviderProps {
    children: ReactNode;
    value: AiciOnboardingContextValue;
}

export const AiciOnboardingProvider = ({ children, value }: AiciOnboardingProviderProps) => {
    return (
        <AiciOnboardingContexte.Provider value={value}>
            {children}
        </AiciOnboardingContexte.Provider>
    );
};
